import React, { useState } from 'react';
import { useCompany } from '../context/CompanyContext'; 
import { 
    ArrowRightIcon, Bars3Icon, XMarkIcon, BoltIcon, CpuChipIcon, ShieldCheckIcon, WrenchScrewdriverIcon 
} from '@heroicons/react/24/outline';

const LandingPage = ({ setPage }) => {
    const { name: companyName, logoUrl, colors, landingConfig, loading } = useCompany(); 
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    if (loading) return <div className="min-h-screen bg-white flex items-center justify-center">Cargando...</div>;

    // --- SAFEGUARD: Colores por defecto si no cargan ---
    const safeColors = colors || { primary: '#2563EB', secondary: '#1E40AF', accent: '#F59E0B', font: '#1F2937' };
    const safeName = companyName || 'BantCore System';
    
    // Estilos dinámicos
    const primaryStyle = { backgroundColor: safeColors.primary, borderColor: safeColors.primary };
    const textPrimaryStyle = { color: safeColors.primary };
    const textSecondaryStyle = { color: safeColors.secondary };

    // --- CORRECCIÓN: Definición de Links de Navegación ---
    const navLinks = [
        { name: 'Inicio', href: '#' },
        { name: 'Nosotros', href: '#about' }, 
        { name: 'Servicios', href: '#services' }, 
        { name: 'Proyectos', href: '#projects' }, 
        { name: 'Contacto', href: '#contact' }
    ];

    // Configuración por defecto si no hay nada en BD
    const defaultConfig = [
        { id: 'hero', label: 'Bienvenida', enabled: true },
        { id: 'services', label: 'Servicios', enabled: true },
        { id: 'about', label: 'Sobre Nosotros', enabled: true },
        { id: 'projects', label: 'Proyectos', enabled: true },
        { id: 'contact', label: 'Contacto', enabled: true }
    ];
    
    // Si landingConfig viene de BD lo usamos, si no, usamos el default
    const sectionsToRender = landingConfig || defaultConfig;

    // --- COMPONENTES DE SECCIÓN ---
    
    const HeroSection = () => (
        <section className="relative bg-gradient-to-b from-slate-900 to-slate-800 text-white pt-24 pb-32">
            <div className="container mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
                <div>
                    <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-6 leading-tight">
                        Soluciones Integrales en <span style={textSecondaryStyle}>Ingeniería y Tecnología</span>
                    </h1>
                    <p className="text-lg text-gray-300 mb-10 max-w-xl leading-relaxed">
                        En <strong>{safeName}</strong> garantizamos la continuidad operativa de su industria o edificio con servicios especializados.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4">
                        <a href="#contact" className="flex items-center justify-center gap-2 text-white px-8 py-3 rounded-lg font-semibold text-lg shadow-lg transform hover:-translate-y-1 transition" style={primaryStyle}>
                            Solicitar Visita <ArrowRightIcon className="w-5 h-5" />
                        </a>
                        <a href="#services" className="flex items-center justify-center gap-2 bg-transparent text-white px-8 py-3 rounded-lg font-semibold text-lg border-2 border-slate-600 hover:bg-slate-800 transition">
                            Ver Servicios
                        </a>
                    </div>
                </div>
                <div className="bg-white/5 backdrop-blur-md border border-white/10 p-8 rounded-2xl shadow-2xl hidden md:block">
                     <h4 className="text-xl font-semibold mb-6 text-white border-b border-white/10 pb-2">Nuestras Especialidades</h4>
                     <ul className="space-y-4">
                        <li className="flex items-center gap-3"><BoltIcon className="w-6 h-6" style={textSecondaryStyle}/><span className="text-gray-200">Proyectos Eléctricos</span></li>
                        <li className="flex items-center gap-3"><CpuChipIcon className="w-6 h-6" style={textSecondaryStyle}/><span className="text-gray-200">Automatización Industrial</span></li>
                        <li className="flex items-center gap-3"><ShieldCheckIcon className="w-6 h-6" style={textSecondaryStyle}/><span className="text-gray-200">Seguridad Electrónica</span></li>
                        <li className="flex items-center gap-3"><WrenchScrewdriverIcon className="w-6 h-6" style={textSecondaryStyle}/><span className="text-gray-200">Mantención Preventiva</span></li>
                     </ul>
                </div>
            </div>
        </section>
    );

    const ServicesSection = () => (
        <section id="services" className="py-24 bg-gray-50 dark:bg-slate-900 transition-colors">
            <div className="container mx-auto px-6">
                <div className="text-center mb-16">
                    <span className="font-bold uppercase tracking-wider text-sm transition-colors" style={textPrimaryStyle}>Lo que hacemos</span>
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mt-2 transition-colors">Excelencia Técnica Garantizada</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {[
                        { title: 'Informes y Auditorías', desc: 'Gestión digital de informes técnicos, levantamientos y certificaciones.', img: 'https://placehold.co/600x400/1e293b/FFFFFF?text=Informes' },
                        { title: 'Ingeniería y Proyectos', desc: 'Diseño, cálculo y ejecución de proyectos llave en mano.', img: 'https://placehold.co/600x400/1e293b/FFFFFF?text=Proyectos' },
                        { title: 'Automatización', desc: 'Control inteligente para optimizar sus procesos productivos.', img: 'https://placehold.co/600x400/1e293b/FFFFFF?text=Control' }
                    ].map((srv, idx) => (
                        <div key={idx} className="bg-white dark:bg-slate-800 rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-slate-700 overflow-hidden group">
                            <div className="h-48 overflow-hidden"><img src={srv.img} alt={srv.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/></div>
                            <div className="p-8"><h3 className="text-xl font-bold mb-3 text-gray-800 dark:text-white transition-colors">{srv.title}</h3><p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed transition-colors">{srv.desc}</p></div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );

    const AboutSection = () => (
        <section id="about" className="py-20 bg-white dark:bg-slate-900 transition-colors">
            <div className="container mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
                <div className="relative">
                    <div className="absolute -inset-4 bg-gradient-to-r from-gray-200 to-gray-100 dark:from-slate-700 dark:to-slate-600 rounded-xl transform -rotate-2 transition-colors"></div>
                    <img src="https://placehold.co/600x400/1e293b/FFFFFF?text=Nuestro+Equipo" alt="Equipo" className="relative rounded-lg shadow-lg w-full" />
                </div>
                <div>
                    <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 transition-colors">Sobre <span style={textPrimaryStyle} className="transition-colors">{safeName}</span></h2>
                    <p className="text-gray-600 dark:text-gray-300 text-lg mb-6 leading-relaxed transition-colors">Somos una empresa comprometida con la calidad y la eficiencia. Utilizamos tecnología de punta para gestionar nuestros procesos.</p>
                    <div className="bg-slate-50 dark:bg-slate-800 p-6 rounded-xl border-l-4 transition-colors" style={{ borderColor: safeColors.primary }}>
                        <p className="text-slate-700 dark:text-slate-200 font-medium transition-colors">"Nuestra plataforma de gestión digital nos permite entregar informes en tiempo real y mantener un historial detallado."</p>
                    </div>
                </div>
            </div>
        </section>
    );

    const ProjectsSection = () => (
        <section id="projects" className="py-20 bg-gray-50 dark:bg-slate-900 transition-colors">
            <div className="container mx-auto px-6 text-center">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 transition-colors">Sectores que Atendemos</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-white dark:bg-slate-800 p-6 rounded shadow hover:shadow-lg transition border-t-4" style={{borderColor: safeColors.secondary}}>
                        <h3 className="font-bold text-lg mb-2 dark:text-white transition-colors">Industria</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-300 transition-colors">Soluciones de fuerza y control para plantas productivas.</p>
                    </div>
                    <div className="bg-white dark:bg-slate-800 p-6 rounded shadow hover:shadow-lg transition border-t-4" style={{borderColor: safeColors.secondary}}>
                         <h3 className="font-bold text-lg mb-2 dark:text-white transition-colors">Minería</h3>
                         <p className="text-sm text-gray-500 dark:text-gray-300 transition-colors">Estándares de alta seguridad y continuidad operativa.</p>
                    </div>
                    <div className="bg-white dark:bg-slate-800 p-6 rounded shadow hover:shadow-lg transition border-t-4" style={{borderColor: safeColors.secondary}}>
                         <h3 className="font-bold text-lg mb-2 dark:text-white transition-colors">Retail</h3>
                         <p className="text-sm text-gray-500 dark:text-gray-300 transition-colors">Iluminación eficiente y sistemas de respaldo.</p>
                    </div>
                </div>
            </div>
        </section>
    );

    const ContactSection = () => (
        <section id="contact" className="py-24 bg-slate-900 text-white transition-colors">
            <div className="container mx-auto px-6 text-center">
                 <h2 className="text-3xl font-bold mb-4">¿Listo para comenzar?</h2>
                 <p className="text-gray-400 mb-10 max-w-2xl mx-auto">Contáctenos hoy mismo para agendar una visita técnica.</p>
                 <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-2xl max-w-lg mx-auto text-left transition-colors">
                     <form onSubmit={(e) => { e.preventDefault(); alert('Mensaje enviado (Simulación)'); }}>
                         <div className="space-y-4">
                             <div><label className="block text-sm font-bold text-gray-700 dark:text-gray-200 mb-1 transition-colors">Nombre</label><input type="text" className="w-full p-3 border dark:border-slate-600 rounded-lg text-gray-900 dark:text-white bg-gray-50 dark:bg-slate-700 focus:ring-2 focus:ring-blue-500 outline-none transition-colors" placeholder="Su nombre"/></div>
                             <div><label className="block text-sm font-bold text-gray-700 dark:text-gray-200 mb-1 transition-colors">Correo</label><input type="email" className="w-full p-3 border dark:border-slate-600 rounded-lg text-gray-900 dark:text-white bg-gray-50 dark:bg-slate-700 focus:ring-2 focus:ring-blue-500 outline-none transition-colors" placeholder="correo@empresa.com"/></div>
                             <button type="submit" className="w-full text-white font-bold py-3 rounded-lg hover:opacity-90 transition shadow-lg mt-2" style={primaryStyle}>Enviar Mensaje</button>
                         </div>
                     </form>
                 </div>
            </div>
        </section>
    );

    // Mapa de componentes para el renderizado dinámico
    const sectionMap = {
        'hero': <HeroSection key="hero" />,
        'services': <ServicesSection key="services" />,
        'about': <AboutSection key="about" />,
        'projects': <ProjectsSection key="projects" />,
        'contact': <ContactSection key="contact" />
    };

    return (
        <div className="bg-white dark:bg-slate-900 min-h-screen text-slate-900 dark:text-gray-100 antialiased font-sans transition-colors">
            {/* Header */}
            <header className="bg-slate-900 shadow-lg sticky top-0 z-50 text-white">
                <nav className="container mx-auto px-6 py-4 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        {logoUrl ? <img src={logoUrl} alt={`Logo ${safeName}`} className="h-10 w-auto rounded-md bg-white p-1 object-contain" /> : <div className="h-10 w-10 bg-white/10 rounded flex items-center justify-center font-bold text-xl">{safeName.charAt(0)}</div>}
                        <div><h1 className="font-bold text-lg text-white leading-none">{safeName}</h1><p className="text-xs text-gray-400">Servicios Profesionales</p></div>
                    </div>
                    <div className="hidden lg:flex items-center space-x-6">
                        {navLinks.map((link) => <a key={link.name} href={link.href} className="text-gray-300 hover:text-white font-medium transition-colors text-sm">{link.name}</a>)}
                        <span className="border-l border-gray-700 h-6"></span>
                        <button onClick={() => setPage('login')} className="text-white px-5 py-2 rounded-lg font-semibold hover:opacity-90 transition shadow-md text-sm" style={primaryStyle}>Acceso Intranet</button>
                    </div>
                    <div className="lg:hidden"><button onClick={() => setIsMobileMenuOpen(true)}><Bars3Icon className="w-6 h-6 text-white" /></button></div>
                </nav>
                {/* Menú Móvil Overlay */}
                {isMobileMenuOpen && (
                    <div className="fixed inset-0 bg-slate-900 z-50 flex flex-col p-6 lg:hidden">
                        <div className="flex justify-between items-center mb-8">
                            <span className="font-bold text-xl text-white">{safeName}</span>
                            <button onClick={() => setIsMobileMenuOpen(false)}><XMarkIcon className="w-6 h-6 text-white" /></button>
                        </div>
                        <div className="flex flex-col space-y-4">
                            {navLinks.map((link) => (
                                <a key={link.name} href={link.href} onClick={() => setIsMobileMenuOpen(false)} className="text-gray-200 hover:text-white font-semibold text-lg py-2 border-b border-gray-800">{link.name}</a>
                            ))}
                            <button onClick={() => { setPage('login'); setIsMobileMenuOpen(false); }} className="text-white w-full px-5 py-3 rounded-lg font-semibold text-lg mt-4" style={primaryStyle}>Acceso Portal</button>
                        </div>
                    </div>
                )}
            </header>

            {/* RENDERIZADO DINÁMICO DE SECCIONES */}
            <main>
                {sectionsToRender.map(section => (
                    section.enabled ? sectionMap[section.id] : null
                ))}
            </main>

            {/* Footer */}
            <footer className="bg-slate-950 text-gray-400 py-12 border-t border-slate-900">
                <div className="container mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-6">
                    <div className="text-center md:text-left">
                        <p className="text-lg font-bold text-white mb-1">{safeName}</p>
                        <p className="text-xs">© {new Date().getFullYear()} BantCore System. Todos los derechos reservados.</p>
                    </div>
                    <div className="text-center md:text-right">
                        <p className="text-xs opacity-50">Plataforma Tecnológica</p>
                        <p className="text-sm font-medium text-gray-300">Desarrollado por <span className="text-white">Bant Ingeniería y Asesoría SpA</span> - v{require('../config/constants').APP_VERSION}</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LandingPage;